<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  deal: { type: Object, required: true },
});

const emit = defineEmits(['open']);

const { locale } = useI18n();

const formattedAmount = computed(() =>
  new Intl.NumberFormat(locale.value, {
    style: 'currency',
    currency: props.deal.currency || 'EUR',
    maximumFractionDigits: 0,
  }).format((props.deal.amountCents || 0) / 100)
);

const formattedCloseDate = computed(() => {
  if (!props.deal.expectedCloseOn) return '';
  return new Intl.DateTimeFormat(locale.value, {
    day: 'numeric',
    month: 'short',
  }).format(new Date(props.deal.expectedCloseOn));
});

// "Le mie società" che seguono il deal (fino a 2), compilate a mano.
const ownerCompanies = computed(() =>
  Array.isArray(props.deal.ownerCompanies) ? props.deal.ownerCompanies : []
);
</script>

<template>
  <div
    class="flex flex-col gap-2 p-3 rounded-lg cursor-grab bg-n-solid-1 outline outline-1 outline-n-container hover:outline-n-slate-6 active:cursor-grabbing"
    @click="emit('open', deal)"
  >
    <div class="flex items-start justify-between gap-2">
      <span class="text-sm font-medium leading-5 text-n-slate-12 line-clamp-2">
        {{ deal.name }}
      </span>
      <span class="text-sm font-semibold shrink-0 text-n-slate-11">
        {{ formattedAmount }}
      </span>
    </div>

    <div
      v-if="ownerCompanies.length"
      class="flex items-center gap-1.5 min-w-0 text-n-slate-11"
    >
      <Icon icon="i-lucide-building-2" class="size-3.5 shrink-0" />
      <span class="text-xs truncate">{{ ownerCompanies.join(', ') }}</span>
    </div>

    <div v-if="deal.contact" class="flex items-center gap-2 min-w-0">
      <Avatar
        :name="deal.contact.name"
        :src="deal.contact.thumbnail"
        :size="16"
        rounded-full
      />
      <span class="text-xs truncate text-n-slate-11">
        {{ deal.contact.name }}
      </span>
    </div>

    <div class="flex items-center gap-3 text-n-slate-10">
      <span v-if="formattedCloseDate" class="flex items-center gap-1 text-xs">
        <Icon icon="i-lucide-calendar" class="size-3" />
        {{ formattedCloseDate }}
      </span>
      <span
        v-if="deal.conversationsCount"
        class="flex items-center gap-1 text-xs"
      >
        <Icon icon="i-lucide-message-circle" class="size-3" />
        {{ deal.conversationsCount }}
      </span>
    </div>
  </div>
</template>
